import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { YoutubeIcon, Moon, Sun, Info, Search, LogIn, LogOut, User, MessageSquare } from 'lucide-react';
import VideoSearchModal from './VideoSearchModal';
import FeedbackModal from './FeedbackModal';
import { User as SupabaseUser } from '@supabase/supabase-js';

interface LayoutProps {
  children: React.ReactNode;
  darkMode: boolean;
  setDarkMode: (value: boolean) => void;
  onVideoSelect?: (videoId: string, title: string) => void;
  onAuthClick: () => void;
  user: SupabaseUser | null;
}

const Layout: React.FC<LayoutProps> = ({ 
  children, 
  darkMode, 
  setDarkMode,
  onVideoSelect,
  onAuthClick,
  user
}) => {
  const location = useLocation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  useEffect(() => {
    const root = window.document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [darkMode]);

  const handleVideoSelect = (videoId: string, title: string) => {
    if (onVideoSelect) {
      onVideoSelect(videoId, title);
    }
  };

  return (
    <div className="font-sans min-h-screen flex flex-col bg-light-background dark:bg-dark-background text-light-text dark:text-dark-text transition-colors duration-300">
      {/* Header */}
      <header className="bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border shadow-sm sticky top-0 z-10">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center group">
              <YoutubeIcon size={28} className="text-red-600 mr-2 group-hover:scale-110 transition-transform" />
              <h1 className="text-xl font-bold bg-gradient-to-r from-red-500 to-purple-600 bg-clip-text text-transparent group-hover:from-red-600 group-hover:to-purple-700 transition-colors">
                YouTube<span className="font-bold">Player</span>
              </h1>
            </Link>
            <div className="flex items-center space-x-3 sm:space-x-4">
              <Link
                to="/about"
                className={`p-2 rounded-lg transition-colors ${ 
                  location.pathname === '/about'
                    ? 'bg-light-accent/10 text-light-accent dark:bg-dark-accent/20 dark:text-dark-accent'
                    : 'text-light-subtle dark:text-dark-subtle hover:bg-black/5 dark:hover:bg-white/10'
                }`}
                title="About"
              >
                <Info size={20} />
              </Link>
              <button
                onClick={() => setIsSearchOpen(true)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Search videos"
              >
                <Search size={20} className="text-gray-600 dark:text-gray-300" />
              </button>
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? (
                  <Sun size={20} className="text-gray-600 dark:text-gray-300" />
                ) : (
                  <Moon size={20} className="text-gray-600 dark:text-gray-300" />
                )}
              </button>
              <button
                onClick={() => setIsFeedbackOpen(true)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                aria-label="Open feedback form"
              >
                <MessageSquare size={20} className="text-gray-600 dark:text-gray-300" />
                <span className="hidden sm:block text-sm font-medium">Feedback</span>
              </button>
              {user ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium hidden sm:block">
                    {user.email}
                  </span>
                  <button
                    onClick={onAuthClick}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                  >
                    <LogOut size={20} className="text-gray-600 dark:text-gray-300" />
                    <span className="sr-only">Sign Out</span>
                  </button>
                </div>
              ) : (
                <button
                  onClick={onAuthClick}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                >
                  <LogIn size={20} className="text-gray-600 dark:text-gray-300" />
                  <span className="hidden sm:block text-sm font-medium">Sign In</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-light-card dark:bg-dark-card border-t border-light-border dark:border-dark-border py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center justify-center space-y-4 text-center">
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 w-full sm:w-auto">
              <span className="mb-2 sm:mb-0">Made by</span>
              <a
                href="https://x.com/kevindgtl"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-light-accent dark:text-dark-accent hover:text-light-accent_hover dark:hover:text-dark-accent_hover underline underline-offset-2 transition-colors"
              >
                @kevindgtl
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Video Search Modal */}
      <VideoSearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onVideoSelect={handleVideoSelect}
      />

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
      />
    </div>
  );
};

export default Layout;